<script setup lang="ts">
import { computed } from "vue";

type DetailCardPinType = "aanbieder" | "activiteit" | "speelplek" | "wandelroute" | "fietsroute";

interface OverlayPin {
    id: number;
    title: string;
    type: DetailCardPinType;
    kind: "activity" | "play";
    latitude: number;
    longitude: number;
    sportmomentId?: number;
    aanbiederId?: string;
    displayLabel?: string;
    bookingType?: number;
}

const props = withDefaults(defineProps<{
    pin: OverlayPin;
    isOpen: boolean;
    isActive: boolean;
    isMobile?: boolean;
    pinColor: string;
    iconClass: string;
    description?: string;
    address?: string;
    imageUrl?: string;
    isLoadingDetails?: boolean;
    infoUrl?: string;
    routeDistance?: number | null;
    routeInspanning?: string[];
    routeTerreintype?: string[];
    bookingUrl?: string;
    externalBookingUrl?: string;
    waitlistUrl?: string;
    bookingType?: number;
    showBookButton?: boolean;
    showWaitlist?: boolean;
}>(), {
    description: "",
    isMobile: false,
    isLoadingDetails: false,
    routeDistance: null,
    routeInspanning: () => [],
    routeTerreintype: () => [],
    showBookButton: false,
    showWaitlist: false,
});

const emit = defineEmits<{
    (e: "toggle"): void;
    (e: "focus-pin"): void;
}>();

const typeLabels: Record<DetailCardPinType, string> = {
    aanbieder: "Sportaanbieder",
    fietsroute: "Fietsroute",
    speelplek: "Speelplek",
    wandelroute: "Wandelroute",
    activiteit: "Activiteit",
};

const typeLabel = computed(() => {
    if (props.pin.displayLabel) {
        return props.pin.displayLabel;
    }
    return typeLabels[props.pin.type] ?? props.pin.type;
});

const showHeaderImage = computed(() => {
    return Boolean(props.imageUrl);
});

const showBottomImage = computed(() => {
    if (!props.imageUrl) {
        return false;
    }

    return props.pin.type !== "aanbieder" && props.pin.kind !== "activity";
});

const isRoutePin = computed(() => {
    return props.pin.type === "wandelroute" || props.pin.type === "fietsroute";
});

const formattedRouteDistance = computed(() => {
    if (props.routeDistance === null || props.routeDistance === undefined || !Number.isFinite(props.routeDistance)) {
        return "";
    }

    const formattedDistance = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 1 }).format(props.routeDistance);
    return `~ ${formattedDistance} km`;
});

const routeInspanningText = computed(() => {
    return (props.routeInspanning ?? []).filter(Boolean).join(", ");
});

const routeTerreintypeText = computed(() => {
    return (props.routeTerreintype ?? []).filter(Boolean).join(", ");
});

const hasRouteMeta = computed(() => {
    return Boolean(formattedRouteDistance.value || routeInspanningText.value || routeTerreintypeText.value);
});

</script>

<template>
    <div v-if="!isOpen" class="interactieve-kaart__detail-card" :class="{ 'is-active': isActive }"
        @click="emit('toggle')">
        <img v-if="showHeaderImage && imageUrl" :src="imageUrl" loading="lazy" decoding="async"
            class="interactieve-kaart__detail-card-header-image interactieve-kaart__detail-card-header-image--closed" />
        <div class="interactieve-kaart__detail-card-summary">
            <div class="interactieve-kaart__detail-card-badge" :style="{ backgroundColor: pinColor }">
                <span class="icon" :class="iconClass"></span>
                <span>{{ typeLabel }}</span>
            </div>
            <p class="interactieve-kaart__detail-card-title mb-0">{{ pin.title }}</p>
            <p class="interactieve-kaart__detail-card-address mb-0">{{ address || "Locatie niet beschikbaar" }}</p>
        </div>
    </div>

    <div v-else class="interactieve-kaart__detail-card is-open" :class="{ 'is-active': isActive }">
        <div class="interactieve-kaart__detail-card-header">
            <img v-if="showHeaderImage && imageUrl" :src="imageUrl" loading="lazy" decoding="async"
                class="interactieve-kaart__detail-card-header-image interactieve-kaart__detail-card-header-image--open" />
            <div class="interactieve-kaart__detail-card-summary">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="interactieve-kaart__detail-card-badge" :style="{ backgroundColor: pinColor }">
                        <span class="icon" :class="iconClass"></span>
                        <span>{{ typeLabel }}</span>
                    </div>
                    <span class="icon-close" role="button" tabindex="0" @click="emit('toggle')"></span>
                </div>
                <p class="interactieve-kaart__detail-card-title mb-0">{{ pin.title }}</p>
                <div class="interactieve-kaart__detail-card-address-wrapper">
                    <p class="interactieve-kaart__detail-card-address mb-0">{{ address || "Locatie niet beschikbaar" }}</p>
                </div>
            </div>
        </div>

        <div class="interactieve-kaart__detail-card-content">
            <p v-if="isLoadingDetails" class="small text-muted">Details laden...</p>
            <template v-else>
            <div v-if="isRoutePin && hasRouteMeta" class="interactieve-kaart__detail-card-route-meta">
                <p v-if="formattedRouteDistance" class="interactieve-kaart__detail-card-route-line"><strong>Afstand:</strong> {{ formattedRouteDistance }}</p>
                <p v-if="routeInspanningText" class="interactieve-kaart__detail-card-route-line"><strong>Inspanning:</strong> {{ routeInspanningText }}</p>
                <p v-if="routeTerreintypeText" class="interactieve-kaart__detail-card-route-line"><strong>Terreintype:</strong> {{ routeTerreintypeText }}</p>
            </div>
            <div v-if="description" class="interactieve-kaart__detail-card-description" v-html="description" />

            <p v-if="!description && !(isRoutePin && hasRouteMeta)" class="small text-muted">Geen aanvullende beschrijving beschikbaar.</p>
            </template>
            <img v-if="showBottomImage && imageUrl" :src="imageUrl" loading="lazy" decoding="async" />
        </div>

        <div class="interactieve-kaart__detail-card-footer">
            <a v-if="infoUrl" :href="infoUrl" target="_self" class="btn btn-sm btn-sport-in-light">
                Informatie
            </a>
            <button v-else class="btn btn-sm btn-sport-in-light" @click="emit('focus-pin')">
                Informatie
            </button>

            <a
                v-if="pin.kind === 'activity' && showBookButton && bookingUrl"
                :href="bookingUrl"
                target="_self"
                class="btn btn-sm btn-outline-primary"
            >
                Boeken
            </a>

            <a
                v-else-if="pin.kind === 'activity' && pin.bookingType === 2 && externalBookingUrl"
                :href="externalBookingUrl"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
            >
                Extern boeken
            </a>

            <a
                v-else-if="pin.kind === 'activity' && showWaitlist && waitlistUrl"
                :href="waitlistUrl"
                target="_self"
                class="btn btn-sm btn-outline-primary"
            >
                Wachtrij
            </a>
        </div>
    </div>
</template>